(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════
     Country / Language modal — Louis Vuitton–style.

     - Shows once per browser on first visit if IP geolocation resolves
       to a country different from the store's currently active one.
     - Also opens from the footer "Ship to" trigger (manual mode).
     - Confirming submits Shopify's native localization form, which is
       the single source of truth the footer, cart, and checkout all
       read from on the next page load.
     - Language pills only appear when the SELECTED country's market has
       more than one published language (driven live by Shopify Markets
       via country.available_languages — nothing hardcoded here).

     IMPORTANT: the very first line below runs synchronously (this script
     is `defer`, so it executes before DOMContentLoaded fires). That lets
     cookie-consent.liquid check window.__jaffaryGeoModalPending and, if
     true, hold off showing the cookie banner until this modal resolves.
     ═══════════════════════════════════════════════════════════════════ */

  /* ── Behaviour switches ──────────────────────────────────────────
     ALWAYS_ASK_FIRST_VISIT
       true  = show the modal once to every new visitor, so they always
               get to confirm/choose (matches "it pops up and the
               customer chooses country").
       false = only interrupt when IP geolocation disagrees with the
               country the store is currently rendering (closer to LV's
               "You are visiting from X — update?" behaviour).

     Testing: append ?geo=1 to any URL to force the modal open,
     ignoring the saved flag entirely. ?geo=debug also logs its
     decision-making to the console. Neither affects real visitors.
     ── */
  var ALWAYS_ASK_FIRST_VISIT = true;

  /* Only the markets listed above are selectable. Everything else is hidden.

     TRADEOFF, worth knowing: a visitor from a hidden country (Greece, Israel,
     Singapore…) can't pick their own country, so they stay on the primary
     market — United Kingdom / GBP. They can still buy and still ship there;
     they just see GBP rather than their local currency. That's roughly 5% of
     orders based on the last 500. Flip this to true to list every country
     Shopify offers, alphabetically, after the primary ones. */
  var SHOW_ALL_COUNTRIES = false;

  /* Current locale prefix, e.g. "/" or "/de-de/". */
  function jGeoRoot() {
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    } catch (e) {}
    return '/';
  }

  var RESOLVED_KEY = 'jaffary_geo_modal_resolved'; /* has the visitor ever seen/confirmed/dismissed this? */
  var qs = '';
  try { qs = window.location.search || ''; } catch (e) {}
  var FORCE_OPEN = /[?&]geo=(1|debug)/.test(qs);
  var DEBUG = /[?&]geo=debug/.test(qs);
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[geo-modal]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  var alreadyResolved = false;
  try { alreadyResolved = !!localStorage.getItem(RESOLVED_KEY); } catch (e) {}
  if (FORCE_OPEN) alreadyResolved = false;
  window.__jaffaryGeoModalPending = !alreadyResolved;

  /* ── Primary markets, ordered by ACTUAL ORDER VOLUME ──
     Derived from a 500-order sample of paid orders:
       US 113 · DE 97 · NL 72 · GB 58 · FR 31 · IT 13 · CA 13 · JP 12
       DK 12 · AU 11 · SE 10 · HK 9 · CH 8 · AT 8 · FI 5 · ES 4 · BE 3
     The top five alone are ~74% of all orders, so they sit at the very top
     where they need no scrolling. Everything else drops into the
     "International" group at the bottom — still fully selectable, just not
     cluttering the list for the countries that almost never order.

     BR is included despite low order volume because it has its own market
     with published Portuguese; hiding it would bury the Portuguese pills.

     MX is listed on request. NOTE: there is no Mexico market in Shopify yet,
     so Mexico currently falls under International and shows English only.
     To get Spanish pills there, create a Mexico market and publish Spanish
     for it — then MX in COUNTRY_LOCAL_LANGS below starts working on its own.

     To re-rank later, just reorder this array. Countries not listed here
     are handled automatically. */
  var PRIMARY_MARKETS = [
    'US', 'DE', 'NL', 'GB', 'FR',
    'IT', 'CA', 'JP', 'DK', 'AU',
    'SE', 'HK', 'CH', 'AT', 'FI',
    'ES', 'BE', 'BR', 'MX'
  ];

  function countryCodeToFlag(code) {
    if (!code || code.length !== 2) return '\uD83C\uDF10';
    var points = [];
    for (var i = 0; i < 2; i++) points.push(0x1F1E6 + code.toUpperCase().charCodeAt(i) - 65);
    return String.fromCodePoint.apply(String, points);
  }

  function getCountries() {
    var el = document.querySelector('[data-geo-modal-countries]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function findCountry(iso) {
    var list = getCountries();
    for (var i = 0; i < list.length; i++) { if (list[i].i === iso) return list[i]; }
    return null;
  }

  /* ── Build the country list once ──
       A clickable row list (not a native <select>), styled to match the
       footer: flag, country name, currency. Only the markets in
       PRIMARY_MARKETS are offered — see SHOW_ALL_COUNTRIES below. */
  var selectedIso = null;
  var listBuilt = false;
  /* Country Shopify's geolocation reported for this visitor, if any.
     Used when they pick "International": rather than pinning everyone to one
     arbitrary country, we send their REAL country, which the International
     market converts to local currency automatically. */
  var geoDetectedIso = null;
  var INTL = '__INTL__';

  function setSelected(iso) {
    selectedIso = iso;
    var list = document.querySelector('[data-geo-modal-list]');
    if (list) {
      list.querySelectorAll('.geo-modal__country').forEach(function (row) {
        var on = row.getAttribute('data-iso') === iso;
        row.classList.toggle('is-active', on);
        row.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
    var countryInput = document.querySelector('[data-geo-modal-country-input]');
    if (iso === INTL) {
      /* International is always English, so never show language pills. */
      var field = document.querySelector('[data-geo-modal-lang-field]');
      var langInput = document.querySelector('[data-geo-modal-language-input]');
      if (field) field.hidden = true;
      if (langInput) langInput.value = 'en';
      return;
    }
    if (countryInput) countryInput.value = iso;
    renderPills(iso);
  }

  function buildSelect() {
    var list = document.querySelector('[data-geo-modal-list]');
    if (!list || listBuilt) return;
    var countries = getCountries();

    var show = [];
    PRIMARY_MARKETS.forEach(function (iso) {
      var c = findCountry(iso);
      if (c) show.push(c);
    });

    if (SHOW_ALL_COUNTRIES) {
      var used = {};
      show.forEach(function (c) { used[c.i] = true; });
      countries
        .filter(function (c) { return !used[c.i]; })
        .sort(function (a, b) { return a.n.localeCompare(b.n); })
        .forEach(function (c) { show.push(c); });
    }

    show.forEach(function (c) {
      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'geo-modal__country';
      row.setAttribute('data-iso', c.i);
      row.setAttribute('role', 'option');
      row.innerHTML =
        '<span class="geo-modal__country-flag" aria-hidden="true">' + countryCodeToFlag(c.i) + '</span>' +
        '<span class="geo-modal__country-name"></span>' +
        '<span class="geo-modal__country-cur"></span>';
      /* textContent, not innerHTML, for the data-driven parts */
      row.querySelector('.geo-modal__country-name').textContent = c.n;
      row.querySelector('.geo-modal__country-cur').textContent = c.c + ' ' + c.s;
      row.addEventListener('click', function () { setSelected(c.i); });
      list.appendChild(row);
    });

    /* One catch-all row, LV-style, instead of listing ~190 countries. */
    var intl = document.createElement('button');
    intl.type = 'button';
    intl.className = 'geo-modal__country geo-modal__country--intl';
    intl.setAttribute('data-iso', INTL);
    intl.setAttribute('role', 'option');
    intl.innerHTML =
      '<span class="geo-modal__country-flag" aria-hidden="true">\uD83C\uDF10</span>' +
      '<span class="geo-modal__country-name">International (English)</span>' +
      '<span class="geo-modal__country-cur"></span>';
    intl.addEventListener('click', function () { setSelected(INTL); });
    list.appendChild(intl);

    log('country list built —', show.length, 'markets + International');
    listBuilt = true;
  }

  /* ── Which second language legitimately belongs to each country ──
     Countries inheriting the primary web presence report its locales
     (e.g. English + German) even where German is irrelevant — Netherlands,
     Japan, UAE, etc. This map is the guard: a second language is only ever
     offered if it's actually that country's own language. Everything not
     listed here stays English-only, no pills.
     Add a country here only when you publish its language in Markets.
     Netherlands is deliberately absent: it stays English-only by choice,
     even if Dutch is published later. Don't add NL here.

     RULE: only list a language here if translated content ACTUALLY EXISTS
     for it. Your five translated languages are de, fr, it, es, pt-BR.
     Speculative entries (Arabic, Japanese, Korean, Chinese, Nordic, Polish)
     were removed on purpose: publishing one of those locales without
     translating the products would otherwise show a pill that leads to an
     untranslated store, which reads worse than plain English. Translate
     first, then add the country here. */
  var COUNTRY_LOCAL_LANGS = {
    DE: ['de'], AT: ['de'], CH: ['de', 'fr', 'it'],
    FR: ['fr'], BE: ['fr'], CA: ['fr'],
    IT: ['it'],
    ES: ['es'], MX: ['es'], AR: ['es'], CL: ['es'], CO: ['es'], PR: ['es'],
    BR: ['pt-BR']
  };

  /* Uppercase the FIRST character only. Liquid's `capitalize` would lowercase
     everything after it, turning "português (Brasil)" into "(brasil)" and
     "中文" style labels into mush. This preserves internal capitals. */
  function labelFor(name) {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  function allowedLangsFor(iso, langs) {
    var local = COUNTRY_LOCAL_LANGS[iso] || [];
    return langs.filter(function (l) {
      return l.c === 'en' || local.indexOf(l.c) !== -1;
    });
  }

  /* ── Language pills: only shown when the selected country's market
       actually publishes more than one language ── */
  function renderPills(iso) {
    var field = document.querySelector('[data-geo-modal-lang-field]');
    var wrap = document.querySelector('[data-geo-modal-pills]');
    var langInput = document.querySelector('[data-geo-modal-language-input]');
    if (!field || !wrap || !langInput) return;
    var country = findCountry(iso);
    var langs = allowedLangsFor(iso, (country && country.l) || []);
    log('languages offered for', iso, '→', langs.map(function (l) { return l.c; }).join(', ') || 'English only');
    wrap.innerHTML = '';
    if (langs.length < 2) {
      field.hidden = true;
      /* No pills for this country — but the hidden input may still hold the
         language the visitor is CURRENTLY browsing in (e.g. they were on
         Deutsch and just picked Netherlands). Submitting that pair would ask
         for a language this market doesn't publish. Reset it explicitly to
         the one language available here, defaulting to English. */
      langInput.value = (langs[0] && langs[0].c) || 'en';
      log('no pills — language forced to', langInput.value);
      return;
    }
    field.hidden = false;
    /* default to English if this market publishes it, else the first listed language */
    var defaultLang = langs.some(function (l) { return l.c === 'en'; }) ? 'en' : langs[0].c;
    langInput.value = defaultLang;
    langs.forEach(function (l) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'geo-modal__pill' + (l.c === defaultLang ? ' is-active' : '');
      pill.textContent = labelFor(l.n);
      pill.setAttribute('data-lang', l.c);
      pill.addEventListener('click', function () {
        wrap.querySelectorAll('.geo-modal__pill').forEach(function (p) { p.classList.remove('is-active'); });
        pill.classList.add('is-active');
        langInput.value = l.c;
      });
      wrap.appendChild(pill);
    });
  }

  /* ── Open / close ── */
  function openModal(opts) {
    opts = opts || {};
    var modal = document.getElementById('geo-modal');
    if (!modal) return;
    buildSelect();

    var title = document.querySelector('[data-geo-modal-title]');
    var body = document.querySelector('[data-geo-modal-body]');
    var countryInput = document.querySelector('[data-geo-modal-country-input]');
    if (!countryInput) return;

    var prefillIso = opts.detectedIso || opts.manualIso || countryInput.value;
    setSelected(prefillIso); /* highlights the row, syncs the hidden input, renders pills */

    if (opts.detectedIso && opts.detectedName) {
      /* Geolocation disagreed with the current country — ask LV-style, naming it. */
      if (title) title.textContent = title.getAttribute('data-title-change') || 'Change shipping country?';
      if (body) {
        var tpl = body.getAttribute('data-body-template') || '';
        /* The placeholder sits inside the translated sentence, so word order
           stays correct in every language. */
        body.textContent = tpl.replace('__COUNTRY__', opts.detectedName);
        body.hidden = false;
      }
    } else {
      /* First-visit confirmation, or manual open from the footer. */
      if (title) title.textContent = title.getAttribute('data-title-choose') || 'Choose your country/region';
      if (body) body.hidden = true;
    }

    modal.hidden = false;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
  }

  function closeModal() {
    var modal = document.getElementById('geo-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    setTimeout(function () { modal.hidden = true; }, 250);
  }

  function markResolved() {
    try { localStorage.setItem(RESOLVED_KEY, '1'); } catch (e) {}
    window.__jaffaryGeoModalPending = false;
    document.dispatchEvent(new CustomEvent('jaffary:geo-modal-resolved'));
  }

  function confirmSelection() {
    var countryInput = document.querySelector('[data-geo-modal-country-input]');
    if (!countryInput) { log('ABORT — country input missing'); return; }

    var form = countryInput.closest('form');
    if (!form) { log('ABORT — country localization form not found in DOM'); return; }

    var iso = selectedIso || countryInput.value;

    if (iso === INTL) {
      /* "International" isn't a country Shopify can accept. Send the visitor's
         REAL detected country instead — the International market has local
         currency conversion enabled, so they get their own currency in English.
         If geolocation gave us nothing usable, submit nothing and just close:
         Shopify's own default is a better guess than an arbitrary country. */
      if (geoDetectedIso && findCountry(geoDetectedIso)) {
        iso = geoDetectedIso;
        log('International → using detected country', iso);
      } else {
        log('International selected but no detected country — closing without change');
        markResolved();
        closeModal();
        return;
      }
    }

    countryInput.value = iso;

    /* Shopify applies only ONE selector per localization submit, so we submit
       the COUNTRY (which sets market + currency) and steer the redirect to the
       chosen language's root_url. That lands on the right market and the right
       locale in a single request — submitting a second form is impossible,
       since the first navigates away. */
    var langInput = document.querySelector('[data-geo-modal-language-input]');
    var wantedLang = langInput && langInput.value;
    var country = findCountry(iso);
    var langs = (country && country.l) || [];
    var target = null;
    for (var i = 0; i < langs.length; i++) {
      if (langs[i].c === wantedLang && langs[i].u) { target = langs[i].u; break; }
    }
    if (target) {
      /* target is the market's ROOT url (e.g. "/de-de/"). Sending the visitor
         there would bounce them to the homepage from wherever they were, which
         is jarring mid-browse. Instead, rebuild the CURRENT path under the new
         locale prefix: /products/x → /de-de/products/x. */
      var currentPath = window.location.pathname;
      var currentRoot = jGeoRoot();
      if (currentRoot !== '/' && currentPath.indexOf(currentRoot) === 0) {
        currentPath = '/' + currentPath.slice(currentRoot.length);
      }
      /* Shopify.routes.root is sometimes reported as "/" even while the
         visitor is on a prefixed market (e.g. /en-de/). The strip above then
         does nothing and the old prefix survives, producing /de-de/en-de/...
         which 404s. Defensively remove ANY known market prefix, taken from
         the same root_url data the modal already renders — so we only ever
         strip real prefixes, never a legitimate page handle. */
      var knownPrefixes = [];
      var allCountries = getCountries();
      for (var ci = 0; ci < allCountries.length; ci++) {
        var cLangs = allCountries[ci].l || [];
        for (var li = 0; li < cLangs.length; li++) {
          if (!cLangs[li].u) continue;
          var p = cLangs[li].u.replace(/^https?:\/\/[^/]+/, '');
          if (p.charAt(p.length - 1) !== '/') p += '/';
          if (p !== '/' && knownPrefixes.indexOf(p) === -1) knownPrefixes.push(p);
        }
      }
      for (var pi = 0; pi < knownPrefixes.length; pi++) {
        var pref = knownPrefixes[pi];
        /* Match the prefix WITH its trailing slash ("/en-de/products/x") and
           also the bare form with nothing after it ("/en-de"), which is what
           the browser reports on a market homepage. Missing the bare case
           left the old prefix in place and produced /de-de/en-de → 404. */
        if (currentPath.indexOf(pref) === 0) {
          currentPath = '/' + currentPath.slice(pref.length);
          break;
        }
        var bare = pref.slice(0, -1); /* "/en-de/" → "/en-de" */
        if (currentPath === bare) {
          currentPath = '/';
          break;
        }
      }
      currentPath = currentPath.replace(/^\/+/, '');
      var newRoot = target.replace(/^https?:\/\/[^/]+/, ''); /* absolute → path */
      if (newRoot.charAt(newRoot.length - 1) !== '/') newRoot += '/';
      var dest = (newRoot + currentPath).replace(/\/{2,}/g, '/');

      /* Preserve the query string, but DROP our own ?geo= testing parameter.
         Carrying it across the redirect would force the modal open again on
         the next page, which loops forever and makes Confirm look broken. */
      var qsKeep = (window.location.search || '')
        .replace(/[?&]geo=(1|debug)/g, '')
        .replace(/^&/, '?');
      if (qsKeep === '?' || qsKeep === '') qsKeep = '';
      dest += qsKeep;

      var rt = form.querySelector('input[name="return_to"]');
      if (rt) {
        rt.value = dest;
        log('return_to →', dest, '(stays on current page)');
      }

      /* Shopify's /localization endpoint prepends the destination market's
         prefix to return_to itself. Passing an already-prefixed path makes it
         prefix a second time (/de-de/en-de/... or /en-de/de-de/...), which
         404s. The URL prefix alone already selects BOTH the market and the
         locale, so navigating straight there is correct and avoids the
         double-prefix entirely. */
      try {
        localStorage.setItem('jaffary_locale_set', '1');
        localStorage.setItem('jaffary_locale_iso', iso);
      } catch (e) {}
      markResolved();
      log('navigating →', dest);
      window.location.href = dest;
      return;
    } else {
      /* No dedicated URL prefix for this market (US, UK, NL and others share
         the root domain). Shopify's own return_to is fine here, but it carries
         the full current query string — including our ?geo= testing flag, which
         would force the modal open again after the redirect. Rewrite it. */
      log('no root_url for', wantedLang, '— using current path');
      var rtDefault = form.querySelector('input[name="return_to"]');
      if (rtDefault) {
        var clean = window.location.pathname +
          (window.location.search || '')
            .replace(/[?&]geo=(1|debug)/g, '')
            .replace(/^&/, '?');
        if (clean.slice(-1) === '?') clean = clean.slice(0, -1);
        rtDefault.value = clean;
        log('return_to →', clean);
      }
    }

    log('submitting → country:', iso, '| language:', wantedLang, '| action:', form.getAttribute('action'));
    try {
      localStorage.setItem('jaffary_locale_set', '1');
      localStorage.setItem('jaffary_locale_iso', iso);
    } catch (e) {}
    markResolved();
    form.submit();
  }

  function wireModal() {
    var modal = document.getElementById('geo-modal');
    if (!modal) return;
    var closeBtn = document.querySelector('[data-geo-modal-close]');
    var overlay = document.querySelector('[data-geo-modal-overlay]');
    var confirmBtn = document.querySelector('[data-geo-modal-confirm]');

    if (confirmBtn) confirmBtn.addEventListener('click', confirmSelection);
    [closeBtn, overlay].forEach(function (el) {
      if (!el) return;
      el.addEventListener('click', function () { markResolved(); closeModal(); });
    });

    /* Footer "Ship to" button(s) — same modal, manual mode, no auto-flag */
    document.querySelectorAll('[data-ship-to-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var iso = btn.getAttribute('data-country-iso') || (document.querySelector('[data-geo-modal-country-input]') || {}).value;
        openModal({ manualIso: iso });
      });
    });
  }

  /* ── First-visit prompt: only ASK, never silently switch ──
       Hard timeout: if the request is slow, proceed after 4s
       instead of leaving the cookie banner blocked indefinitely. */
  function maybeAutoOpen() {
    if (alreadyResolved) {
      log('suppressed — already resolved on a previous visit. Use ?geo=1 to force.');
      markResolved(); /* still fires the resolved event so the cookie banner is unblocked */
      return;
    }
    var countryInput = document.querySelector('[data-geo-modal-country-input]');
    if (!countryInput) {
      log('ABORT — the localization form did not render. Is the modal snippet included?');
      markResolved();
      return;
    }
    var currentIso = countryInput.value;
    log('current store country:', currentIso, '| available countries:', getCountries().length);

    if (FORCE_OPEN) { log('forced open via ?geo='); openModal({}); return; }

    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var settled = false;
    function proceed(detectedIso, detectedName) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (detectedIso) geoDetectedIso = detectedIso;
      var match = detectedIso && findCountry(detectedIso);
      log('detected:', detectedIso || '(none)', '| in your Markets:', !!match);

      if (match && detectedIso !== currentIso) {
        /* Geo disagrees with what we're rendering: ask, LV-style, naming the country. */
        log('opening — detected country differs from current');
        openModal({ detectedIso: detectedIso, detectedName: match.n || detectedName });
        return;
      }
      if (ALWAYS_ASK_FIRST_VISIT) {
        /* Country already correct (or undetectable), but this visitor has
           never chosen: still let them confirm/change once. */
        log('opening — first visit, confirm-or-change prompt');
        openModal({ manualIso: currentIso });
        return;
      }
      log('not opening — country already correct and ALWAYS_ASK_FIRST_VISIT is off');
      markResolved();
    }

    var timer = setTimeout(function () {
      log('geolocation timed out after 4s');
      if (controller) controller.abort();
      proceed('', '');
    }, 4000);

    /* Shopify's OWN localization endpoint — same domain, no third party.
       Uses the visitor's IP + browser settings, served from Shopify's CDN.
       Returns a country suggestion only when it differs from the current one. */
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var current = (window.Shopify && window.Shopify.country) || currentIso || '';
    var url = root + 'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' + encodeURIComponent(current);

    fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var c = data && data.detected_values && data.detected_values.country;
        proceed(((c && c.handle) || '').toUpperCase(), c && c.name);
      })
      .catch(function () {
        log('Shopify localization suggestion unavailable');
        proceed('', ''); /* still offer the prompt rather than blocking the cookie banner */
      });
    /* NOTE: resolution (and unblocking the cookie banner) happens on
       Confirm or Close — not here — so the modal can't be skipped silently. */
  }

  function init() {
    log('init | modal in DOM:', !!document.getElementById('geo-modal'),
        '| ship-to triggers found:', document.querySelectorAll('[data-ship-to-toggle]').length);
    wireModal();
    /* Keep the static flag emoji on any footer button in sync with the current country */
    document.querySelectorAll('[data-ship-to-flag]').forEach(function (el) {
      el.textContent = countryCodeToFlag(el.getAttribute('data-country-iso'));
    });
    maybeAutoOpen();
  }

  /* Console helper for testing:
       JaffaryGeoModal.open()   → open it right now
       JaffaryGeoModal.reset()  → clear the saved flag, then reload to see the first-visit prompt again
       JaffaryGeoModal.status() → what the script currently sees */
  window.JaffaryGeoModal = {
    open: function (iso) { openModal(iso ? { manualIso: iso } : {}); },
    reset: function () {
      try {
        localStorage.removeItem(RESOLVED_KEY);
        localStorage.removeItem('jaffary_locale_set');
        localStorage.removeItem('jaffary_locale_iso');
      } catch (e) {}
      return 'Cleared. Reload the page to see the first-visit prompt.';
    },
    status: function () {
      var input = document.querySelector('[data-geo-modal-country-input]');
      var form = input && input.closest('form');
      return {
        modalInDom: !!document.getElementById('geo-modal'),
        localizationFormRendered: !!input,
        formFound: !!form,
        formAction: form ? form.getAttribute('action') : null,
        formId: form ? form.id : null,
        currentCountry: input ? input.value : null,
        currentLanguage: (document.querySelector('[data-geo-modal-language-input]') || {}).value || null,
        availableCountries: getCountries().length,
        alreadyResolvedFlag: (function () { try { return !!localStorage.getItem(RESOLVED_KEY); } catch (e) { return 'unreadable'; } })(),
        shipToTriggers: document.querySelectorAll('[data-ship-to-toggle]').length
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();